import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { useCallback, useLayoutEffect, useState } from "react";
import { applySignedInDefault } from "@/lib/theme";
import { useIdleTimer } from "@/hooks/useIdleTimer";
import { SessionExpiryWarning } from "@/components/SessionExpiryWarning";
import { ToastProvider } from "@/components/ui/toast";
import { NotificationsProvider } from "@/contexts/NotificationsContext";
import NotificationPopups from "@/components/NotificationPopups";
import NotificationLoginPopup from "@/components/NotificationLoginPopup";
import Loader from "@/components/ui/loader";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import AuthPage from "@/pages/Auth";
import HomePage from "@/pages/Home";
import ListingDetailPage from "@/pages/ListingDetail";
import CreateListingPage from "@/pages/CreateListing";
import MyListingsPage from "@/pages/MyListings";
import DeletedListingsPage from "@/pages/DeletedListings";
import ProfilePage from "@/pages/Profile";
import AdminDashboardPage from "@/pages/AdminDashboard";
import AdminUsersPage from "@/pages/AdminUsers";
import AdminListingsPage from "@/pages/AdminListings";
import AdminLoginPage from "@/pages/AdminLogin";
import EditListingPage from "@/pages/EditListing";
import AdminInterestsPage from "@/pages/AdminInterests";
import LegalOnboardingPage from "@/pages/LegalOnboarding";
import SignNdaPage from "@/pages/SignNda";
import TermsPage from "@/pages/Terms";
import UnlockPaymentPage from "@/pages/UnlockPaymentPage";
import BuyerInterestsPage from "@/pages/BuyerInterests";
import SupportTicketsPage from "@/pages/SupportTickets";
import AdminSupportTicketsPage from "@/pages/AdminSupportTickets";
import AdminAllListingsPage from "@/pages/AdminAllListings";
import AdminBuyerActivityPage from "@/pages/AdminBuyerActivity";
import AdminBuyerFeedbackPage from "@/pages/AdminBuyerFeedback";
import AdminSellerContactsPage from "@/pages/AdminSellerContacts";
import AdminBuyerLeadsPage from "@/pages/AdminBuyerLeads";
import SellerLandingPage from "@/pages/SellerLanding";
import ChoosePlanPage from "@/pages/ChoosePlan";
import FavoriteListingsPage from "@/pages/FavoriteListings";
import AdminAnnouncementsPage from "@/pages/AdminAnnouncements";
import AdminReleaseNotesPage from "@/pages/AdminReleaseNotes";
import AdminUploadNdaPage from "@/pages/AdminUploadNda";
import AdminLeadPipelinePage from "@/pages/AdminLeadPipeline";
import AdvisorAssignmentsPage from "@/pages/AdvisorAssignments";
import ComingSoonPage from "@/pages/ComingSoon";
import OrgLandingPage from "@/pages/OrgLanding";
import PostHogPageTracker from "@/components/PostHogPageTracker";
import BuyerPreferencesModal from "@/components/BuyerPreferencesModal";
import AnnouncementPopup from "@/components/AnnouncementPopup";
import BLAdvisorDashboard from "@/components/dashboards/BLAdvisorDashboard";
import BLAdvisorReferralsPage from "@/pages/BLAdvisorReferrals";
import AdminReferralsPage from "@/pages/AdminReferrals";
import AdminFundingApplicationsPage from "@/pages/AdminFundingApplications";
import BuyerShell from "@/components/BuyerShell";
import AdminShell from "@/components/AdminShell";
import AdvisorShell from "@/components/AdvisorShell";
import RoleShell from "@/components/RoleShell";
import BuyerFeaturePage from "@/pages/BuyerFeaturePage";
import DealPipelineBoard from "@/pages/DealPipelineBoard";
import UnsubscribePage from "@/pages/Unsubscribe";
import UserDirectoryPage from "@/pages/UserDirectory";
import SbaCalculatorPage from "@/pages/SbaCalculator";
import ValuationCalculatorPage from "@/pages/ValuationCalculator";
import FinancialModelPage from "@/pages/FinancialModel";
import FundingApplyPage from "@/pages/FundingApply";
import FundingMarketplacePage from "@/pages/FundingMarketplace";
import AiDealScoringPage from "@/pages/AiDealScoring";
import BuyerDashboardPage from "@/pages/BuyerDashboard";
import GlossaryPage from "@/pages/resources/Glossary";
import FaqPage from "@/pages/resources/Faq";
import NewsPage from "@/pages/resources/News";
import NewsArticlePage from "@/pages/resources/NewsArticle";
import PressPage from "@/pages/resources/Press";
import MastermindPage from "@/pages/resources/Mastermind";
import AdminResourceContentPage from "@/pages/AdminResourceContent";
import AcademyPage from "@/pages/academy/Academy";
import MembersOnlyGate from "@/components/MembersOnlyGate";
import FinanceDashboardPage from "@/pages/FinanceDashboard";
import UtilizationDashboardPage from "@/pages/UtilizationDashboard";
import SuperAdminLoginPage from "@/super-admin/SuperAdminLogin";
import SuperAdminHomePage from "@/super-admin/SuperAdminHome";
import SuperAdminRoute from "@/super-admin/SuperAdminRoute";
import SuperAdminShell from "@/super-admin/SuperAdminShell";
import SuperAdminOrgsPage from "@/super-admin/SuperAdminOrgs";
import SuperAdminResourcesPage from "@/super-admin/SuperAdminResources";
import SuperAdminAcademyPage from "@/super-admin/SuperAdminAcademy";
import SuperAdminChatbotPage from "@/super-admin/SuperAdminChatbot";
import SuperAdminPromoCodesPage from "@/super-admin/SuperAdminPromoCodes";
import SuperAdminTiersPage from "@/super-admin/SuperAdminTiers";
import SuperAdminModulesPage from "@/super-admin/SuperAdminModules";
import SuperAdminNdaPage from "@/super-admin/SuperAdminNda";
import SuperAdminReleaseNotesPage from "@/super-admin/SuperAdminReleaseNotes";
import SuperAdminNotificationRecipientsPage from "@/super-admin/SuperAdminNotificationRecipients";
import SuperAdminListingCategoriesPage from "@/super-admin/SuperAdminListingCategories";
import SuperAdminFinancePage from "@/super-admin/SuperAdminFinance";
import SuperAdminUtilizationPage from "@/super-admin/SuperAdminUtilization";
import ChatbotBubble from "@/components/ChatbotBubble";
import { NivaProvider } from "@/contexts/NivaContext";
import { Activity, AlertTriangle, BookOpen, Calculator, CircleDollarSign, FileText, GraduationCap, KanbanSquare, LineChart, Phone, Rocket, Scale, ShieldCheck } from "lucide-react";
import { GuestProvider } from "@/contexts/GuestContext";
import GuestAccessPage from "@/pages/guest/GuestAccess";
import GuestListingsPage from "@/pages/guest/GuestListings";
import GuestListingDetailPage from "@/pages/guest/GuestListingDetail";
import PublicDealPage, { ShortDealRedirect } from "@/pages/public/PublicDeal";
import SharedListingsPage from "@/pages/SharedListings";
import { homePathForUser, safeRedirectPath } from "@/lib/roleHome";
import { RESOURCE_PAGE_ROLES } from "@/lib/resources";
import { ACADEMY_PAGE_ROLES } from "@/lib/academy";
import { orgDoorPath, orgScopedPath, rememberedOrgDoor } from "@/lib/orgDoor";
import ModuleRoute from "@/components/ModuleRoute";
import { ADMIN_ROLES, PLATFORM_ADMIN_ROLES, type Role } from "@/lib/roles";

/**
 * Role areas that exist in both bare and organization-prefixed form
 * (`/buyer/home` and `/ecfa/buyer/home`). Only these may be canonicalised —
 * redirecting a shared path like /listings/:id under an org prefix would hit
 * no route at all.
 */
const ORG_PREFIXABLE_AREAS = ["/buyer/", "/seller/", "/bl-advisor/", "/advisor/", "/admin/", "/resources/"];

/**
 * The sign-in URL to bounce an unauthenticated visitor to, remembering where
 * they were actually headed as `?next=`.
 *
 * Action links mailed to users — "Review & sign the NDA" → /sign-nda, a deal
 * link, an interest reply — are opened in browsers with no live session all the
 * time (a different device, or after the idle timeout). Without carrying the
 * destination through the login hop, signing in drops them on their role home
 * and the link they clicked is a dead end: the page they were sent to is never
 * reached, and nothing in the app links to it.
 */
function loginPathWithNext(loginPath: string, location: { pathname: string; search: string }): string {
  const door = orgDoorPath(loginPath, rememberedOrgDoor());
  const target = `${location.pathname}${location.search}`;
  // "/" already resolves to the role home after login — nothing to carry.
  if (target === "/") return door;
  return `${door}?next=${encodeURIComponent(target)}`;
}

function RoleRoute({ children, roles, loginPath = "/sign-in" }: { children: React.ReactNode; roles: Role[]; loginPath?: string }) {
  const { user, loading, hasRole } = useAuth();
  const location = useLocation();
  // Signed-in surfaces default to Light·Gold; the landing page and auth screens
  // stay Dark·Gold. No-op once the user has picked a theme themselves. Runs
  // before the early returns below — hooks can't sit behind a conditional.
  // useLayoutEffect, not useEffect: the sign-in screen is dark, so applying this
  // after paint would flash a dark app shell for a frame on the way in.
  useLayoutEffect(() => {
    if (user) applySignedInDefault();
  }, [user]);
  if (loading)
    return <Loader fullScreen />;
  // Send them back to the door they actually signed in through. An org member
  // bounced to the main site's door would be rejected there with an error
  // deliberately identical to a wrong password, and have no way to tell why.
  if (!user) return <Navigate to={loginPathWithNext(loginPath, location)} replace />;
  if (user.needs_terms_acceptance || user.needs_nda_signature) return <Navigate to="/legal-onboarding" replace />;
  if (!hasRole(...roles)) return <Navigate to="/" replace />;
  // Canonicalise the URL: a member of an organization always works under its
  // prefix. Without this, arriving on a bare path — an old bookmark, a link
  // from before the prefixes existed, or hand-typing — silently leaves an ECFA
  // user on /buyer/home instead of /ecfa/buyer/home.
  if (
    user.org_slug &&
    !location.pathname.startsWith(`/${user.org_slug}/`) &&
    ORG_PREFIXABLE_AREAS.some((area) => location.pathname.startsWith(area))
  ) {
    return (
      <Navigate
        to={`/${user.org_slug}${location.pathname}${location.search}`}
        replace
      />
    );
  }
  // Buyers without an active subscription are NOT redirected to choose-plan:
  // free (Basic) buyers can browse the whole site. Member-only content shows
  // in-place Upgrade CTAs that link to /choose-plan instead.
  return <>{children}</>;
}

/**
 * Redirect that keeps whatever organization prefix the current URL carries.
 * `/ecfa/admin` → `/ecfa/admin/home`, `/admin` → `/admin/home`, and the same
 * for the buyer / seller / advisor areas.
 */
function OrgRedirect({ to }: { to: string }) {
  const { orgSlug } = useParams();
  return <Navigate to={orgSlug ? `/${orgSlug}${to}` : to} replace />;
}

function AuthRoute({ children }: { children: React.ReactNode }) {
  const { user, loading, logout } = useAuth();
  const location = useLocation();
  if (loading)
    return <Loader fullScreen />;
  if (user)
    if (user.needs_terms_acceptance || user.needs_nda_signature) return <Navigate to="/legal-onboarding" replace />;
  // Already signed in and carrying a destination (an emailed action link
  // followed in a tab that turned out to have a live session): take them there
  // rather than showing the "Session Active" dead end.
  if (user) {
    const next = safeRedirectPath(new URLSearchParams(location.search).get("next"));
    if (next) return <Navigate to={next} replace />;
  }
  if (user)
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted px-4 py-12">
        <div className="w-full max-w-md text-center space-y-6">
          <div className="flex items-center justify-center h-14 w-14 rounded-xl bg-amber-100 text-amber-600 mx-auto">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
          </div>
          <h2 className="text-2xl font-bold text-foreground">Session Active</h2>
          <p className="text-muted-foreground">
            You are already logged in as <span className="font-semibold text-foreground">{user.first_name} {user.last_name}</span> ({user.email}).
            <br />Please logout first to sign in with a different account.
          </p>
          <div className="flex flex-col gap-3">
            <button
              onClick={() => { logout(); }}
              className="inline-flex items-center justify-center rounded-lg bg-red-600 px-6 py-2.5 text-sm font-semibold text-white hover:bg-red-700 transition-colors"
            >
              Logout &amp; Continue
            </button>
            <a
              href="/"
              className="text-primary font-semibold hover:underline underline-offset-2 text-sm"
            >
              Go to Dashboard
            </a>
          </div>
        </div>
      </div>
    );
  return <>{children}</>;
}

function BrowseGuard({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading)
    return <Loader fullScreen />;
  // Logged-in users never see the bare "/" landing — always forward them to
  // their role-appropriate, shell-wrapped home (sidebar + nav). Anonymous
  // visitors fall through to the public HomePage.
  if (user) {
    if (user.needs_terms_acceptance || user.needs_nda_signature) return <Navigate to="/legal-onboarding" replace />;
    return <Navigate to={homePathForUser(user)} replace />;
  }
  return <>{children}</>;
}

// Guard for the /choose-plan page: must be logged in as a buyer, legal complete, no sub check
function ChoosePlanRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <Loader fullScreen />;
  if (!user) return <Navigate to="/sign-in" replace />;
  if (user.needs_terms_acceptance || user.needs_nda_signature) return <Navigate to="/legal-onboarding" replace />;
  if (!user.roles.includes("buyer")) return <Navigate to="/" replace />;
  // If they already have an active subscription, no need to be here
  if (user.has_active_subscription) return <Navigate to={orgScopedPath("/buyer/home", user.org_slug)} replace />;
  // Buy Box comes before plan selection: a buyer who hasn't completed preferences
  // is sent back to their home, where the Buy Box modal runs, before choose-plan.
  if (user.needs_buyer_preferences) return <Navigate to={orgScopedPath("/buyer/home", user.org_slug)} replace />;
  return <>{children}</>;
}

// Support must stay reachable even while a user is otherwise blocked — pending
// legal onboarding (Terms/NDA) or without an active subscription. This guard
// deliberately skips those redirects; it only requires an authenticated portal
// user so a blocked user always has a way to reach the help desk.
function SupportRoute({ children }: { children: React.ReactNode }) {
  const { user, loading, hasRole } = useAuth();
  const location = useLocation();
  if (loading) return <Loader fullScreen />;
  // Carries the destination through login: /sign-nda is only ever reached from
  // an emailed link or a notification, so losing it here strands the user.
  if (!user) return <Navigate to={loginPathWithNext("/sign-in", location)} replace />;
  if (!hasRole("buyer", "seller", "bl_advisor", ...ADMIN_ROLES)) return <Navigate to="/" replace />;
  return <>{children}</>;
}

// Send the generic /profile path to the current user's role-prefixed profile.
function ProfileRedirect() {
  const { user, loading, hasRole } = useAuth();
  if (loading) return <Loader fullScreen />;
  if (!user) return <Navigate to="/sign-in" replace />;
  if (hasRole(...ADMIN_ROLES)) return <Navigate to={orgScopedPath("/admin/settings/profile", user.org_slug)} replace />;
  if (hasRole("bl_advisor")) return <Navigate to={orgScopedPath("/bl-advisor/settings/profile", user.org_slug)} replace />;
  if (hasRole("seller")) return <Navigate to={orgScopedPath("/seller/profile", user.org_slug)} replace />;
  return <Navigate to={orgScopedPath("/buyer/settings/profile", user.org_slug)} replace />;
}

function LegalReadyRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  if (!user) return <Navigate to="/sign-in" replace />;
  if (!user.needs_terms_acceptance && !user.needs_nda_signature) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function BuyerPreferencesGate() {
  const { user } = useAuth();
  if (!user || user.needs_terms_acceptance || user.needs_nda_signature) return null;
  if (!user.needs_buyer_preferences) return null;
  return <BuyerPreferencesModal />;
}

function AnnouncementGate() {
  const { user } = useAuth();
  // Only show the login announcement once the user is past legal onboarding and
  // buyer-preferences gating, so dialogs don't stack on top of each other.
  if (!user || user.needs_terms_acceptance || user.needs_nda_signature) return null;
  if (user.needs_buyer_preferences) return null;
  return <AnnouncementPopup key={user.id} />;
}

/**
 * "While you were away" digest, shown once per sign-in to every signed-in role
 * — buyer, seller, BL Advisor and admin alike.
 *
 * Same onboarding gating as AnnouncementGate so dialogs don't stack, and it
 * sits one layer below the announcement (z-58 vs z-60) — if both are pending,
 * the announcement is read first and the digest is waiting underneath.
 */
function NotificationDigestGate() {
  const { user } = useAuth();
  if (!user || user.needs_terms_acceptance || user.needs_nda_signature) return null;
  if (user.needs_buyer_preferences) return null;
  return <NotificationLoginPopup key={user.id} />;
}

/**
 * Owns the chatbot's mount lifecycle so its transcript can never outlive the
 * session that produced it.
 *
 * Both halves matter. Returning null here UNMOUNTS the bubble on sign-out,
 * which is what actually discards its conversation state — ChatbotBubble's own
 * internal `if (!user) return null` runs after its hooks, so React kept the
 * component mounted and its `turns` state alive, and the next user to sign in
 * on the same tab inherited the previous user's transcript. And key={user.id}
 * forces a fresh instance when the account changes without an unmount in
 * between (token swap / re-auth), which a plain unmount-on-null wouldn't catch.
 * Same shape as AnnouncementGate above.
 */
function ChatbotGate() {
  const { user } = useAuth();
  if (!user) return null;
  return <ChatbotBubble key={user.id} />;
}

const SESSION_TIMEOUT_MS = (Number(import.meta.env.VITE_SESSION_INACTIVITY_MINUTES) || 60) * 60_000;
const WARN_BEFORE_MS = Math.min(60_000, SESSION_TIMEOUT_MS / 2);

function SessionGuard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showWarning, setShowWarning] = useState(false);

  const handleIdle = useCallback(() => {
    setShowWarning(false);
    logout();
    try { sessionStorage.setItem("session_expired", "1"); } catch { /* ignore */ }
    navigate("/sign-in", { replace: true });
  }, [logout, navigate]);

  const handleWarn = useCallback(() => setShowWarning(true), []);
  const handleActive = useCallback(() => setShowWarning(false), []);

  useIdleTimer({
    timeoutMs: SESSION_TIMEOUT_MS,
    onIdle: handleIdle,
    onWarn: handleWarn,
    onActive: handleActive,
    warnBeforeMs: WARN_BEFORE_MS,
    enabled: !!user,
  });

  if (!showWarning || !user) return null;
  return <SessionExpiryWarning secondsRemaining={Math.round(WARN_BEFORE_MS / 1000)} />;
}

function AppRoutes() {
  return (
    <Routes>
      {/* RoleShell adds the role sidebar for logged-in users and falls back to
          plain navigation for anonymous visitors. */}
      <Route path="/legal/terms" element={<RoleShell><TermsPage /></RoleShell>} />
      <Route
        path="/legal-onboarding"
        element={
          <LegalReadyRoute>
            <Navigation />
            <LegalOnboardingPage />
            <Footer />
          </LegalReadyRoute>
        }
      />
      {/* On-demand NDA signing, reached from the "Send NDA" email/notification.
          Buyers are exempt from the onboarding NDA gate, so SupportRoute (auth
          only, no legal/subscription redirect) lets them reach this page. */}
      <Route
        path="/sign-nda"
        element={
          <SupportRoute>
            <Navigation />
            <SignNdaPage />
            <Footer />
          </SupportRoute>
        }
      />
      {/* Public no-login unsubscribe landing (reached from notification emails) */}
      <Route path="/unsubscribe/:token" element={<UnsubscribePage />} />
      {/* Guest area — share recipients with no account. Deliberately outside
          RoleRoute/RoleShell: these visitors have no member session, and every
          member affordance would be a dead end for them. Access is proven with
          an emailed code, not a login. */}
      <Route path="/shared/:token" element={<GuestAccessPage />} />
      <Route path="/guest/listings" element={<GuestListingsPage />} />
      <Route path="/guest/listings/:id" element={<GuestListingDetailPage />} />
      {/* Public deal card — the "Copy public link" target. No login, no org
          prefix: the link is pasted to outsiders and must open as-is. */}
      <Route path="/deal/:id" element={<PublicDealPage />} />
      {/* /d/:id is the copied link. In production frontend/vercel.json sends it
          to the API's link-preview page (Open Graph tags for WhatsApp etc.),
          which redirects people here. This route only catches it in local dev. */}
      <Route path="/d/:id" element={<ShortDealRedirect />} />
      <Route path="/sign-in" element={<AuthRoute><AuthPage /></AuthRoute>} />
      <Route path="/register" element={<AuthRoute><AuthPage /></AuthRoute>} />
      <Route path="/choose-plan" element={<ChoosePlanRoute><BuyerShell><ChoosePlanPage /></BuyerShell></ChoosePlanRoute>} />
      <Route path="/auth" element={<Navigate to="/sign-in" replace />} />
      <Route path="/Sign-in" element={<Navigate to="/sign-in" replace />} />
      {/* The buyer's landing page (see homePathForUser). /buyer/home keeps the
          marketplace it has always been — the dashboard links into it. */}
      <Route
        path="/:orgSlug?/buyer/dashboard"
        element={
          <RoleRoute roles={["buyer", ...ADMIN_ROLES]}>
            <BuyerShell><BuyerDashboardPage /></BuyerShell>
          </RoleRoute>
        }
      />
      <Route
        path="/:orgSlug?/buyer/home"
        element={
          <RoleRoute roles={["buyer", ...ADMIN_ROLES]}>
            <BuyerShell><HomePage /></BuyerShell>
          </RoleRoute>
        }
      />
      <Route
        path="/:orgSlug?/seller/home"
        element={
          <RoleRoute roles={["seller", ...ADMIN_ROLES]}>
            <Navigation />
            <HomePage />
            <Footer />
          </RoleRoute>
        }
      />
      <Route
        path="/:orgSlug?/bl-advisor/home"
        element={
          <RoleRoute roles={["bl_advisor", ...ADMIN_ROLES]}>
            <AdvisorShell><HomePage /></AdvisorShell>
          </RoleRoute>
        }
      />

      {/* Buyer role-prefixed routes */}
      <Route path="/:orgSlug?/buyer/interests" element={<RoleRoute roles={["buyer", ...ADMIN_ROLES]}><BuyerShell><BuyerInterestsPage /></BuyerShell></RoleRoute>} />
      <Route path="/:orgSlug?/buyer/favorites" element={<RoleRoute roles={["buyer", ...ADMIN_ROLES]}><BuyerShell><FavoriteListingsPage /></BuyerShell></RoleRoute>} />
      <Route path="/:orgSlug?/buyer/support" element={<RoleRoute roles={["buyer", ...ADMIN_ROLES]}><BuyerShell><SupportTicketsPage /></BuyerShell></RoleRoute>} />

      {/* Buyer sidebar feature pages (placeholder experiences) */}
      <Route path="/:orgSlug?/buyer/concierge-intros" element={<RoleRoute roles={["buyer", ...ADMIN_ROLES]}><BuyerShell><BuyerFeaturePage title="Concierge Intros" icon={Phone} description="Warm introductions to brokers and sellers, handled by our concierge team." /></BuyerShell></RoleRoute>} />
      <Route path="/:orgSlug?/buyer/deal-scoring" element={<RoleRoute roles={["buyer", ...ADMIN_ROLES]}><BuyerShell><AiDealScoringPage /></BuyerShell></RoleRoute>} />
      <Route path="/:orgSlug?/buyer/pipeline" element={<RoleRoute roles={["buyer", ...ADMIN_ROLES]}><BuyerShell><MembersOnlyGate requires="operator" title="Deal Pipeline CRM" icon={KanbanSquare} description="Track every deal from interest to close on your personal pipeline board. The Deal CRM is an Operator-plan feature."><DealPipelineBoard /></MembersOnlyGate></BuyerShell></RoleRoute>} />
      <Route path="/:orgSlug?/buyer/due-diligence" element={<RoleRoute roles={["buyer", ...ADMIN_ROLES]}><BuyerShell><BuyerFeaturePage title="Due Diligence" icon={ShieldCheck} badge="New" description="Organize documents, checklists, and findings for each deal." /></BuyerShell></RoleRoute>} />
      <Route path="/:orgSlug?/buyer/risk-flags" element={<RoleRoute roles={["buyer", ...ADMIN_ROLES]}><BuyerShell><BuyerFeaturePage title="AI Risk Flags" icon={AlertTriangle} badge="AI" description="Automated risk signals surfaced from listing and financial data." /></BuyerShell></RoleRoute>} />
      <Route path="/:orgSlug?/buyer/sba-calculator" element={<RoleRoute roles={["buyer", ...ADMIN_ROLES]}><BuyerShell><MembersOnlyGate title="SBA Acquisition Calculator" icon={Calculator} description="Model debt service, cash required, DSCR, and SBA 7(a) eligibility on any deal. The calculator is reserved for members."><SbaCalculatorPage /></MembersOnlyGate></BuyerShell></RoleRoute>} />
      <Route path="/:orgSlug?/buyer/valuation-calculator" element={<RoleRoute roles={["buyer", ...ADMIN_ROLES]}><BuyerShell><MembersOnlyGate title="Business Valuation Calculator" icon={Scale} description="Work out SDE, EBITDA and a defensible valuation range for any business. The calculator is reserved for members."><ValuationCalculatorPage /></MembersOnlyGate></BuyerShell></RoleRoute>} />
      <Route path="/:orgSlug?/buyer/financial-model" element={<RoleRoute roles={["buyer", ...ADMIN_ROLES]}><BuyerShell><MembersOnlyGate title="5-Year Financial Model" icon={LineChart} description="Build a 5-year acquisition pro forma with free cash flow, DSCR, exit and IRR, and stress-test it. The model is reserved for members."><FinancialModelPage /></MembersOnlyGate></BuyerShell></RoleRoute>} />
      {/* Funding Marketplace — the Debt Placement Pre-Qualification form.
          Reached as the sole child of the Funding Marketplace nav group. */}
      <Route path="/:orgSlug?/buyer/funding/:partnerSlug" element={<RoleRoute roles={["buyer", ...ADMIN_ROLES]}><BuyerShell><MembersOnlyGate title="Funding Marketplace" icon={CircleDollarSign} description="Apply for acquisition financing through our funding partners. Reserved for members."><FundingApplyPage /></MembersOnlyGate></BuyerShell></RoleRoute>} />
      <Route path="/:orgSlug?/buyer/funding" element={<RoleRoute roles={["buyer", ...ADMIN_ROLES]}><BuyerShell><MembersOnlyGate title="Funding Marketplace" icon={CircleDollarSign} description="Apply for acquisition financing through our funding partners. Reserved for members."><FundingMarketplacePage /></MembersOnlyGate></BuyerShell></RoleRoute>} />
      <Route path="/:orgSlug?/buyer/valuation-comps" element={<RoleRoute roles={["buyer", ...ADMIN_ROLES]}><BuyerShell><BuyerFeaturePage title="Valuation & Comps" icon={Activity} badge="New" description="Benchmark asking prices against comparable deals and multiples." /></BuyerShell></RoleRoute>} />
      <Route path="/:orgSlug?/buyer/loi-templates" element={<RoleRoute roles={["buyer", ...ADMIN_ROLES]}><BuyerShell><BuyerFeaturePage title="LOI Templates" icon={FileText} badge="New" description="Ready-to-use Letter of Intent templates to move quickly on a deal." /></BuyerShell></RoleRoute>} />
      {/* Old per-role placeholder library (hardcoded cards, dead links). The
          real library is the shared /resources pages, so old bookmarks land there. */}
      <Route path="/:orgSlug?/buyer/resources" element={<OrgRedirect to="/resources" />} />

      {/* Seller role-prefixed routes */}
      <Route path="/:orgSlug?/seller/favorites" element={<RoleRoute roles={["seller", ...ADMIN_ROLES]}><Navigation /><FavoriteListingsPage /><Footer /></RoleRoute>} />
      <Route path="/:orgSlug?/seller/listings" element={<RoleRoute roles={["seller", ...ADMIN_ROLES]}><Navigation /><MyListingsPage /><Footer /></RoleRoute>} />
      <Route path="/:orgSlug?/seller/new-listing" element={<RoleRoute roles={["seller", ...ADMIN_ROLES]}><Navigation /><CreateListingPage /><Footer /></RoleRoute>} />
      <Route path="/:orgSlug?/seller/interests" element={<RoleRoute roles={["seller", ...ADMIN_ROLES]}><Navigation /><AdminInterestsPage /><Footer /></RoleRoute>} />
      <Route path="/:orgSlug?/seller/support" element={<RoleRoute roles={["seller", ...ADMIN_ROLES]}><Navigation /><SupportTicketsPage /><Footer /></RoleRoute>} />

      {/* BL Advisor role-prefixed routes */}
      <Route path="/:orgSlug?/bl-advisor/dashboard" element={<RoleRoute roles={["bl_advisor", ...ADMIN_ROLES]}><AdvisorShell><BLAdvisorDashboard /></AdvisorShell></RoleRoute>} />
      <Route path="/:orgSlug?/bl-advisor/advisors" element={<RoleRoute roles={["bl_advisor", ...ADMIN_ROLES]}><AdvisorShell><UserDirectoryPage kind="advisors" /></AdvisorShell></RoleRoute>} />
      <Route path="/:orgSlug?/bl-advisor/buyers" element={<RoleRoute roles={["bl_advisor", ...ADMIN_ROLES]}><AdvisorShell><UserDirectoryPage kind="buyers" /></AdvisorShell></RoleRoute>} />
      <Route path="/:orgSlug?/bl-advisor/referrals" element={<RoleRoute roles={["bl_advisor", ...ADMIN_ROLES]}><AdvisorShell><BLAdvisorReferralsPage /></AdvisorShell></RoleRoute>} />
      <Route path="/:orgSlug?/bl-advisor/shared-listings" element={<RoleRoute roles={["bl_advisor", ...ADMIN_ROLES]}><ModuleRoute module="shared_listings"><AdvisorShell><SharedListingsPage scope="mine" /></AdvisorShell></ModuleRoute></RoleRoute>} />
      <Route path="/:orgSlug?/bl-advisor/favorites" element={<RoleRoute roles={["bl_advisor", ...ADMIN_ROLES]}><AdvisorShell><FavoriteListingsPage /></AdvisorShell></RoleRoute>} />
      <Route path="/:orgSlug?/bl-advisor/listings" element={<RoleRoute roles={["bl_advisor", ...ADMIN_ROLES]}><ModuleRoute module="my_listings"><AdvisorShell><MyListingsPage /></AdvisorShell></ModuleRoute></RoleRoute>} />
      <Route path="/:orgSlug?/bl-advisor/listings/deleted" element={<RoleRoute roles={["bl_advisor", ...ADMIN_ROLES]}><ModuleRoute module="deleted_listings"><AdvisorShell><DeletedListingsPage /></AdvisorShell></ModuleRoute></RoleRoute>} />
      <Route path="/:orgSlug?/bl-advisor/new-listing" element={<RoleRoute roles={["bl_advisor", ...ADMIN_ROLES]}><ModuleRoute module="new_listing"><AdvisorShell><CreateListingPage /></AdvisorShell></ModuleRoute></RoleRoute>} />
      <Route path="/:orgSlug?/bl-advisor/interests" element={<RoleRoute roles={["bl_advisor", ...ADMIN_ROLES]}><AdvisorShell><AdminInterestsPage /></AdvisorShell></RoleRoute>} />
      <Route path="/:orgSlug?/bl-advisor/pipeline" element={<RoleRoute roles={["bl_advisor", ...ADMIN_ROLES]}><AdvisorShell><DealPipelineBoard /></AdvisorShell></RoleRoute>} />
      <Route path="/:orgSlug?/bl-advisor/support" element={<RoleRoute roles={["bl_advisor", ...ADMIN_ROLES]}><AdvisorShell><SupportTicketsPage /></AdvisorShell></RoleRoute>} />
      <Route path="/:orgSlug?/bl-advisor/resources" element={<OrgRedirect to="/resources" />} />

      {/* Public seller acquisition page. Anonymous, like "/" — pinned Dark ·
          Gold with no theme toggle, and the same nav/footer chrome. Ranks above
          the "/:orgSlug" org-landing route because the segment is static. */}
      <Route
        path="/seller"
        element={
          <div data-theme="dark" className="flex min-h-screen flex-col bg-background">
            <Navigation logoVariant="dark" logoLockup />
            <SellerLandingPage />
            <Footer logoVariant="dark" logoLockup />
          </div>
        }
      />
      <Route
        path="/"
        element={
          <BrowseGuard>
            {/* Main page always renders Dark · Gold — data-theme pins the palette
                regardless of the app-wide toggle (the nav hides the toggle here). */}
            {/* data-theme pins Dark·Gold via a nested div, but useTheme() reads
                <html>, so BrandLogo can't infer it — force the dark wordmark
                explicitly (else the light logo's dark text vanishes on dark). */}
            {/* logoLockup: the landing page sets the brand in live text rather
                than the baked-in wordmark image, whose tagline is unreadable
                once downscaled to nav size (see BrandWordmark). */}
            <div data-theme="dark" className="flex min-h-screen flex-col bg-background">
              <Navigation logoVariant="dark" logoLockup />
              <HomePage />
              <Footer logoVariant="dark" logoLockup />
            </div>
          </BrowseGuard>
        }
      />
      <Route
        path="/favorites"
        element={
          <RoleRoute roles={["buyer", "seller", "bl_advisor", ...ADMIN_ROLES]} loginPath="/sign-in">
            <RoleShell><FavoriteListingsPage /></RoleShell>
          </RoleRoute>
        }
      />
      <Route
        path="/my-interests"
        element={
          <RoleRoute roles={["buyer", "seller", "bl_advisor", ...ADMIN_ROLES]}>
            <RoleShell><BuyerInterestsPage /></RoleShell>
          </RoleRoute>
        }
      />
      <Route
        path="/listings/mine"
        element={
          <RoleRoute roles={["seller", "bl_advisor", ...ADMIN_ROLES]}>
            <RoleShell><MyListingsPage /></RoleShell>
          </RoleRoute>
        }
      />
      <Route path="/profile" element={<ProfileRedirect />} />
      {/* Buyer settings — tabbed page (My Profile / Password & Security /
          Email Notifications / Subscription Plan). The old /buyer/profile
          path redirects into it so existing links keep working. */}
      <Route path="/:orgSlug?/buyer/profile" element={<OrgRedirect to="/buyer/settings/profile" />} />
      <Route path="/:orgSlug?/buyer/settings" element={<OrgRedirect to="/buyer/settings/profile" />} />
      <Route
        path="/:orgSlug?/buyer/settings/:tab"
        element={
          <RoleRoute roles={["buyer", ...ADMIN_ROLES]}>
            <BuyerShell><ProfilePage /></BuyerShell>
          </RoleRoute>
        }
      />
      <Route
        path="/:orgSlug?/seller/profile"
        element={
          <RoleRoute roles={["seller", ...ADMIN_ROLES]}>
            <Navigation />
            <ProfilePage />
            <Footer />
          </RoleRoute>
        }
      />
      {/* Advisor + admin settings — the same tabbed page as the buyer's, so the
          account area is one consistent surface. The old /profile paths redirect
          in, keeping existing links and bookmarks working. */}
      <Route path="/:orgSlug?/bl-advisor/profile" element={<OrgRedirect to="/bl-advisor/settings/profile" />} />
      <Route path="/:orgSlug?/bl-advisor/settings" element={<OrgRedirect to="/bl-advisor/settings/profile" />} />
      <Route
        path="/:orgSlug?/bl-advisor/settings/:tab"
        element={
          <RoleRoute roles={["bl_advisor", ...ADMIN_ROLES]}>
            <AdvisorShell><ProfilePage /></AdvisorShell>
          </RoleRoute>
        }
      />
      <Route path="/:orgSlug?/admin/profile" element={<OrgRedirect to="/admin/settings/profile" />} />
      <Route path="/:orgSlug?/admin/settings" element={<OrgRedirect to="/admin/settings/profile" />} />
      <Route
        path="/:orgSlug?/admin/settings/:tab"
        element={
          <RoleRoute roles={[...ADMIN_ROLES]} loginPath="/admin/sign-in">
            <AdminShell><ProfilePage /></AdminShell>
          </RoleRoute>
        }
      />
      <Route
        path="/listings/new"
        element={
          <RoleRoute roles={["seller", "bl_advisor", ...ADMIN_ROLES]}>
            <RoleShell><CreateListingPage /></RoleShell>
          </RoleRoute>
        }
      />
      <Route
        path="/listings/:id/edit"
        element={
          <RoleRoute roles={["seller", "bl_advisor", ...ADMIN_ROLES]}>
            <RoleShell><EditListingPage /></RoleShell>
          </RoleRoute>
        }
      />
      <Route
        path="/listings/:id"
        element={
          // Deal detail requires an account: the financials are the product,
          // and an open page let the whole catalogue be harvested.
          <RoleRoute roles={["buyer", "seller", "bl_advisor", ...ADMIN_ROLES]}>
            <RoleShell><ListingDetailPage /></RoleShell>
          </RoleRoute>
        }
      />
      <Route
        path="/listings/:id/unlock"
        element={
          <RoleRoute roles={["buyer"]}>
            <BuyerShell><UnlockPaymentPage /></BuyerShell>
          </RoleRoute>
        }
      />
      {/* Support stays reachable even while the user is blocked on legal
          onboarding or plan selection (SupportRoute skips those redirects).
          RoleShell keeps the sidebar; a blocked user clicking a gated sidebar
          link is simply bounced back by that route's own guard. */}
      <Route
        path="/support"
        element={
          <SupportRoute>
            <RoleShell><SupportTicketsPage /></RoleShell>
          </SupportRoute>
        }
      />
      {/* Resource pages — one shared set of pages, so they use the role-agnostic
          RoleShell (which picks the right sidebar) rather than role-prefixed
          copies. The bare /resources path redirects into the first child,
          mirroring /buyer/settings.

          Allowed roles come from RESOURCE_PAGE_ROLES rather than a literal per
          route: Seller is excluded until Seller resource functionality ships,
          and re-enabling it is a one-line change in lib/resources.ts.

          The library is member-only, so each page is additionally wrapped in
          MembersOnlyGate: Basic (free) buyers reaching a bookmarked URL get the
          upgrade CTA instead of content, while advisors and admins pass through
          untouched. The API refuses their feed request too — see
          _assert_member_tier in app/routers/resources.py.

          Every path carries the optional :orgSlug? segment for the same reason
          the buyer/advisor/admin areas do: AppSidebar runs every link through
          orgScopedPath, so an ECFA user's Resource link is /ecfa/resources/...
          Without the segment those URLs matched no route at all and fell
          through to the catch-all, bouncing the whole organization back to "/"
          — which read as "the page won't load". */}
      <Route path="/:orgSlug?/resources" element={<OrgRedirect to="/resources/glossary" />} />
      <Route
        path="/:orgSlug?/resources/glossary"
        element={
          <RoleRoute roles={RESOURCE_PAGE_ROLES}>
            <RoleShell><MembersOnlyGate title="Glossary" icon={BookOpen} description="The Resource library — glossary, FAQ and market news — is reserved for members."><GlossaryPage /></MembersOnlyGate></RoleShell>
          </RoleRoute>
        }
      />
      <Route
        path="/:orgSlug?/resources/faq"
        element={
          <RoleRoute roles={RESOURCE_PAGE_ROLES}>
            <RoleShell><MembersOnlyGate title="FAQ" icon={BookOpen} description="The Resource library — glossary, FAQ and market news — is reserved for members."><FaqPage /></MembersOnlyGate></RoleShell>
          </RoleRoute>
        }
      />
      <Route
        path="/:orgSlug?/resources/news"
        element={
          <RoleRoute roles={RESOURCE_PAGE_ROLES}>
            <RoleShell><MembersOnlyGate title="Latest News & Articles" icon={BookOpen} description="The Resource library — glossary, FAQ and market news — is reserved for members."><NewsPage /></MembersOnlyGate></RoleShell>
          </RoleRoute>
        }
      />
      {/* Internal article detail — where Read More goes when an article has no
          external URL. Same role guard as the listing; the API re-checks access
          by id so a typed URL can't reach another role's content. */}
      <Route
        path="/:orgSlug?/resources/news/:id"
        element={
          <RoleRoute roles={RESOURCE_PAGE_ROLES}>
            <RoleShell><MembersOnlyGate title="Latest News & Articles" icon={BookOpen} description="The Resource library — glossary, FAQ and market news — is reserved for members."><NewsArticlePage /></MembersOnlyGate></RoleShell>
          </RoleRoute>
        }
      />
      {/* Press & media coverage. No :id route and no admin screen — the list is
          read from the marketing site's press page by the API, and every card
          links out to the publication that ran the story. Same role guard and
          member gate as the rest of the library; the API applies both again. */}
      <Route
        path="/:orgSlug?/resources/press"
        element={
          <RoleRoute roles={RESOURCE_PAGE_ROLES}>
            <RoleShell><MembersOnlyGate title="Press & Media Coverage" icon={BookOpen} description="The Resource library — glossary, FAQ, market news and press coverage — is reserved for members."><PressPage /></MembersOnlyGate></RoleShell>
          </RoleRoute>
        }
      />
      {/* Mastermind is staff-only content. The API enforces the same rule on the
          feed and on the file stream, so this guard is defence in depth rather
          than the only check. */}
      <Route
        path="/:orgSlug?/resources/mastermind"
        element={
          <RoleRoute roles={["bl_advisor", "admin"]}>
            <RoleShell><MastermindPage /></RoleShell>
          </RoleRoute>
        }
      />
      {/* BL Academy — one page for buyers and BL advisors, so it uses the
          role-agnostic RoleShell rather than a copy per role, exactly like the
          Resource pages above. The :orgSlug? segment is mandatory for the same
          reason they carry it: AppSidebar runs every link through
          orgScopedPath, and without the segment an ECFA user's /ecfa/academy
          would match no route and fall through to the catch-all.

          Member-only, like the Resource library: a Basic (free) buyer reaching a
          bookmarked URL gets the upgrade CTA instead of a page that would fail
          its first request. Advisors and admins pass through untouched. The API
          refuses their feed too — see tier.assert_member_tier, which is the same
          rule both features share. */}
      <Route
        path="/:orgSlug?/academy"
        element={
          <RoleRoute roles={ACADEMY_PAGE_ROLES}>
            <RoleShell><MembersOnlyGate title="BL Academy" icon={GraduationCap} description="BL Academy — the video classroom for buying a business — is reserved for members."><AcademyPage /></MembersOnlyGate></RoleShell>
          </RoleRoute>
        }
      />
      {/* Super admin routes — hardcoded single account, see src/super-admin/ */}
      <Route path="/super-admin/sign-in" element={<SuperAdminLoginPage />} />
      <Route path="/super-admin/login" element={<Navigate to="/super-admin/sign-in" replace />} />
      <Route path="/super-admin" element={<Navigate to="/super-admin/home" replace />} />
      <Route
        path="/super-admin/home"
        element={
          <SuperAdminRoute>
            <SuperAdminShell><SuperAdminHomePage /></SuperAdminShell>
          </SuperAdminRoute>
        }
      />
      <Route
        path="/super-admin/orgs"
        element={
          <SuperAdminRoute>
            <SuperAdminShell><SuperAdminOrgsPage /></SuperAdminShell>
          </SuperAdminRoute>
        }
      />
      <Route
        path="/super-admin/resources"
        element={
          <SuperAdminRoute>
            <SuperAdminShell><SuperAdminResourcesPage /></SuperAdminShell>
          </SuperAdminRoute>
        }
      />
      {/* BL Academy authoring. No platform-admin twin: every write is gated by
          require_super_admin, so there is no second console to mount it in. */}
      <Route
        path="/super-admin/academy"
        element={
          <SuperAdminRoute>
            <SuperAdminShell><SuperAdminAcademyPage /></SuperAdminShell>
          </SuperAdminRoute>
        }
      />
      {/* TKT-00124: the industry list behind every listing's category field. */}
      <Route
        path="/super-admin/listing-categories"
        element={
          <SuperAdminRoute>
            <SuperAdminShell><SuperAdminListingCategoriesPage /></SuperAdminShell>
          </SuperAdminRoute>
        }
      />
      <Route
        path="/super-admin/chatbot"
        element={
          <SuperAdminRoute>
            <SuperAdminShell><SuperAdminChatbotPage /></SuperAdminShell>
          </SuperAdminRoute>
        }
      />
      <Route
        path="/super-admin/promo-codes"
        element={
          <SuperAdminRoute>
            <SuperAdminShell><SuperAdminPromoCodesPage /></SuperAdminShell>
          </SuperAdminRoute>
        }
      />
      {/* Finance + utilization. The same two page components the portal admin
          console mounts, wired to the super admin client — the org scope is
          resolved server-side, so the super admin sees every organization. */}
      <Route
        path="/super-admin/finance"
        element={
          <SuperAdminRoute>
            <SuperAdminShell><SuperAdminFinancePage /></SuperAdminShell>
          </SuperAdminRoute>
        }
      />
      <Route
        path="/super-admin/utilization"
        element={<Navigate to="/super-admin/utilization/buyers" replace />}
      />
      <Route
        path="/super-admin/utilization/buyers"
        element={
          <SuperAdminRoute>
            <SuperAdminShell><SuperAdminUtilizationPage segment="buyer" /></SuperAdminShell>
          </SuperAdminRoute>
        }
      />
      <Route
        path="/super-admin/utilization/advisors"
        element={
          <SuperAdminRoute>
            <SuperAdminShell><SuperAdminUtilizationPage segment="bl_advisor" /></SuperAdminShell>
          </SuperAdminRoute>
        }
      />
      <Route
        path="/super-admin/tiers"
        element={
          <SuperAdminRoute>
            <SuperAdminShell><SuperAdminTiersPage /></SuperAdminShell>
          </SuperAdminRoute>
        }
      />
      <Route
        path="/super-admin/modules"
        element={
          <SuperAdminRoute>
            <SuperAdminShell><SuperAdminModulesPage /></SuperAdminShell>
          </SuperAdminRoute>
        }
      />
      <Route
        path="/super-admin/nda"
        element={
          <SuperAdminRoute>
            <SuperAdminShell><SuperAdminNdaPage /></SuperAdminShell>
          </SuperAdminRoute>
        }
      />
      <Route
        path="/super-admin/release-notes"
        element={
          <SuperAdminRoute>
            <SuperAdminShell><SuperAdminReleaseNotesPage /></SuperAdminShell>
          </SuperAdminRoute>
        }
      />
      <Route
        path="/super-admin/notification-recipients"
        element={
          <SuperAdminRoute>
            <SuperAdminShell><SuperAdminNotificationRecipientsPage /></SuperAdminShell>
          </SuperAdminRoute>
        }
      />

      {/* Admin routes */}
      <Route path="/:orgSlug?/admin/sign-in" element={<AdminLoginPage />} />
      <Route path="/:orgSlug?/admin/login" element={<OrgRedirect to="/admin/sign-in" />} />
      <Route
        path="/:orgSlug?/admin"
        element={<OrgRedirect to="/admin/home" />}
      />
      <Route
        path="/:orgSlug?/admin/home"
        element={
          <RoleRoute roles={[...ADMIN_ROLES]} loginPath="/admin/sign-in">
            <AdminShell><AdminDashboardPage /></AdminShell>
          </RoleRoute>
        }
      />
      <Route
        path="/:orgSlug?/admin/dashboard"
        element={<OrgRedirect to="/admin/home" />}
      />
      <Route
        path="/:orgSlug?/admin/users"
        element={
          <RoleRoute roles={[...ADMIN_ROLES]} loginPath="/admin/sign-in">
            <AdminShell><AdminUsersPage /></AdminShell>
          </RoleRoute>
        }
      />
      <Route
        path="/:orgSlug?/admin/advisors"
        element={
          <RoleRoute roles={[...ADMIN_ROLES]} loginPath="/admin/sign-in">
            <AdminShell><UserDirectoryPage kind="advisors" /></AdminShell>
          </RoleRoute>
        }
      />
      <Route
        path="/:orgSlug?/admin/buyers"
        element={
          <RoleRoute roles={[...ADMIN_ROLES]} loginPath="/admin/sign-in">
            <AdminShell><UserDirectoryPage kind="buyers" /></AdminShell>
          </RoleRoute>
        }
      />
      <Route
        path="/:orgSlug?/admin/listings"
        element={
          <RoleRoute roles={[...ADMIN_ROLES]} loginPath="/admin/sign-in">
            <ModuleRoute module="my_listings"><AdminShell><MyListingsPage /></AdminShell></ModuleRoute>
          </RoleRoute>
        }
      />
      <Route
        path="/:orgSlug?/admin/listings/deleted"
        element={
          <RoleRoute roles={[...ADMIN_ROLES]} loginPath="/admin/sign-in">
            <ModuleRoute module="deleted_listings"><AdminShell><DeletedListingsPage /></AdminShell></ModuleRoute>
          </RoleRoute>
        }
      />
      <Route
        path="/:orgSlug?/admin/moderation"
        element={
          <RoleRoute roles={[...ADMIN_ROLES]} loginPath="/admin/sign-in">
            <ModuleRoute module="moderation"><AdminShell><AdminListingsPage /></AdminShell></ModuleRoute>
          </RoleRoute>
        }
      />
      <Route
        path="/:orgSlug?/admin/new-listing"
        element={
          <RoleRoute roles={[...ADMIN_ROLES]} loginPath="/admin/sign-in">
            <ModuleRoute module="new_listing"><AdminShell><CreateListingPage /></AdminShell></ModuleRoute>
          </RoleRoute>
        }
      />
      <Route
        path="/:orgSlug?/admin/interests"
        element={
          <RoleRoute roles={[...ADMIN_ROLES, "bl_advisor", "seller"]} loginPath="/admin/sign-in">
            <AdminShell><AdminInterestsPage /></AdminShell>
          </RoleRoute>
        }
      />
      <Route
        path="/:orgSlug?/admin/support"
        element={
          <RoleRoute roles={[...ADMIN_ROLES]} loginPath="/admin/sign-in">
            <AdminShell><AdminSupportTicketsPage /></AdminShell>
          </RoleRoute>
        }
      />
      <Route
        path="/:orgSlug?/admin/announcements"
        element={
          <RoleRoute roles={[...PLATFORM_ADMIN_ROLES]} /* platform admin only */ loginPath="/admin/sign-in">
            <AdminShell><AdminAnnouncementsPage /></AdminShell>
          </RoleRoute>
        }
      />
      <Route
        path="/:orgSlug?/admin/all-listings"
        element={
          <RoleRoute roles={[...ADMIN_ROLES]} loginPath="/admin/sign-in">
            <AdminShell><AdminAllListingsPage /></AdminShell>
          </RoleRoute>
        }
      />
      <Route
        path="/:orgSlug?/admin/funding-applications"
        element={
          <RoleRoute roles={[...ADMIN_ROLES]} loginPath="/admin/sign-in">
            <AdminShell><AdminFundingApplicationsPage /></AdminShell>
          </RoleRoute>
        }
      />
      <Route
        path="/:orgSlug?/admin/referrals"
        element={
          <RoleRoute roles={[...ADMIN_ROLES]} loginPath="/admin/sign-in">
            <AdminShell><AdminReferralsPage /></AdminShell>
          </RoleRoute>
        }
      />
      {/* Both admin tiers. scope="all" spans every org's advisors for a platform
          admin; the API narrows an org_admin to their own organization's shares. */}
      <Route
        path="/:orgSlug?/admin/shared-listings"
        element={
          <RoleRoute roles={[...ADMIN_ROLES]} loginPath="/admin/sign-in">
            <ModuleRoute module="shared_listings"><AdminShell><SharedListingsPage scope="all" /></AdminShell></ModuleRoute>
          </RoleRoute>
        }
      />
      {/* Both admin tiers: a tenant's org_admin gets the identical page,
          scoped to their own organization by the API rather than by the route. */}
      <Route
        path="/:orgSlug?/admin/finance"
        element={
          <RoleRoute roles={[...ADMIN_ROLES]} loginPath="/admin/sign-in">
            <AdminShell><FinanceDashboardPage /></AdminShell>
          </RoleRoute>
        }
      />
      <Route
        path="/:orgSlug?/admin/utilization"
        element={<OrgRedirect to="/admin/utilization/buyers" />}
      />
      <Route
        path="/:orgSlug?/admin/utilization/buyers"
        element={
          <RoleRoute roles={[...ADMIN_ROLES]} loginPath="/admin/sign-in">
            <AdminShell><UtilizationDashboardPage segment="buyer" /></AdminShell>
          </RoleRoute>
        }
      />
      <Route
        path="/:orgSlug?/admin/utilization/advisors"
        element={
          <RoleRoute roles={[...ADMIN_ROLES]} loginPath="/admin/sign-in">
            <AdminShell><UtilizationDashboardPage segment="bl_advisor" /></AdminShell>
          </RoleRoute>
        }
      />
      <Route
        path="/:orgSlug?/admin/buyer-activity"
        element={
          <RoleRoute roles={[...ADMIN_ROLES]} loginPath="/admin/sign-in">
            <AdminShell><AdminBuyerActivityPage /></AdminShell>
          </RoleRoute>
        }
      />
      <Route
        path="/:orgSlug?/admin/feedback"
        element={
          <RoleRoute roles={[...ADMIN_ROLES]} loginPath="/admin/sign-in">
            <AdminShell><AdminBuyerFeedbackPage /></AdminShell>
          </RoleRoute>
        }
      />
      <Route
        path="/:orgSlug?/admin/seller-contacts"
        element={
          <RoleRoute roles={[...ADMIN_ROLES]} loginPath="/admin/sign-in">
            <AdminShell><AdminSellerContactsPage /></AdminShell>
          </RoleRoute>
        }
      />
      <Route
        path="/:orgSlug?/admin/buyer-leads"
        element={
          <RoleRoute roles={[...ADMIN_ROLES]} loginPath="/admin/sign-in">
            <AdminShell><AdminBuyerLeadsPage /></AdminShell>
          </RoleRoute>
        }
      />
      <Route
        path="/:orgSlug?/admin/release-notes"
        element={
          <RoleRoute roles={[...PLATFORM_ADMIN_ROLES]} /* platform admin only */ loginPath="/admin/sign-in">
            <AdminShell><AdminReleaseNotesPage /></AdminShell>
          </RoleRoute>
        }
      />
      <Route
        path="/:orgSlug?/admin/upload-nda"
        element={
          <RoleRoute roles={[...PLATFORM_ADMIN_ROLES]} /* platform admin only */ loginPath="/admin/sign-in">
            <AdminShell><AdminUploadNdaPage /></AdminShell>
          </RoleRoute>
        }
      />
      <Route
        path="/:orgSlug?/admin/lead-pipeline"
        element={
          <RoleRoute roles={[...ADMIN_ROLES]} loginPath="/admin/sign-in">
            <ModuleRoute module="coming_soon_deals"><AdminShell><AdminLeadPipelinePage /></AdminShell></ModuleRoute>
          </RoleRoute>
        }
      />
      <Route path="/:orgSlug?/admin/resources" element={<OrgRedirect to="/resources" />} />
      <Route
        path="/admin/resource-content"
        element={
          <RoleRoute roles={["admin"]} loginPath="/admin/sign-in">
            <AdminShell><AdminResourceContentPage /></AdminShell>
          </RoleRoute>
        }
      />
      {/* Legacy admin path → redirect */}
      <Route path="/:orgSlug?/admin/deal-pipeline" element={<OrgRedirect to="/admin/lead-pipeline" />} />
      <Route path="/:orgSlug?/advisor/assignments" element={<OrgRedirect to="/bl-advisor/assignments" />} />
      <Route
        path="/:orgSlug?/bl-advisor/assignments"
        element={
          <RoleRoute roles={["bl_advisor", ...ADMIN_ROLES]}>
            <AdvisorShell><AdvisorAssignmentsPage /></AdvisorShell>
          </RoleRoute>
        }
      />
      <Route
        path="/:orgSlug?/admin/assignments"
        element={
          <RoleRoute roles={[...ADMIN_ROLES]} loginPath="/admin/sign-in">
            <AdminShell><AdvisorAssignmentsPage /></AdminShell>
          </RoleRoute>
        }
      />
      <Route
        path="/:orgSlug?/admin/pipeline"
        element={
          <RoleRoute roles={[...ADMIN_ROLES]} loginPath="/admin/sign-in">
            <AdminShell><DealPipelineBoard /></AdminShell>
          </RoleRoute>
        }
      />
      {/* Roadmap is member-only, matching every entry point (sidebar, top nav,
          footer, dashboard quick link) — a Basic buyer who types the URL gets
          the upgrade panel. Sellers / advisors are never Basic buyers. */}
      <Route
        path="/:orgSlug?/buyer/upcoming-modules"
        element={
          <RoleRoute roles={["buyer", ...ADMIN_ROLES]}>
            <BuyerShell>
              <MembersOnlyGate title="Upcoming Modules" icon={Rocket} description="Preview the tools landing on BusinessLocating next. The product roadmap is reserved for members.">
                <ComingSoonPage />
              </MembersOnlyGate>
            </BuyerShell>
          </RoleRoute>
        }
      />
      <Route
        path="/:orgSlug?/seller/upcoming-modules"
        element={
          <RoleRoute roles={["seller", ...ADMIN_ROLES]}>
            <Navigation />
            <ComingSoonPage />
            <Footer />
          </RoleRoute>
        }
      />
      <Route
        path="/:orgSlug?/bl-advisor/upcoming-modules"
        element={
          <RoleRoute roles={["bl_advisor", ...ADMIN_ROLES]}>
            <AdvisorShell><ComingSoonPage /></AdvisorShell>
          </RoleRoute>
        }
      />
      {/* Old /coming-soon paths kept as redirects: the page was renamed to
          "Upcoming Modules" (the Coming Soon *deals* are a tab on the home
          marketplace), and bookmarks/emails still point at the old URLs. */}
      <Route path="/:orgSlug?/buyer/coming-soon" element={<OrgRedirect to="/buyer/upcoming-modules" />} />
      <Route path="/:orgSlug?/seller/coming-soon" element={<OrgRedirect to="/seller/upcoming-modules" />} />
      <Route path="/:orgSlug?/bl-advisor/coming-soon" element={<OrgRedirect to="/bl-advisor/upcoming-modules" />} />
      <Route path="/coming-soon" element={<OrgRedirect to="/buyer/upcoming-modules" />} />
      <Route path="/upcoming-modules" element={<OrgRedirect to="/buyer/upcoming-modules" />} />

      {/* Organization doors — the same auth screens as the main site, reached
          under an org prefix. The slug is read off the URL by lib/orgDoor.ts
          and sent with the login/registration request, which is what decides
          which org a new user joins and which users a door admits. Declared
          before /:orgSlug so the two-segment paths win. */}
      <Route path="/:orgSlug/sign-in" element={<AuthRoute><AuthPage /></AuthRoute>} />
      <Route path="/:orgSlug/register" element={<AuthRoute><AuthPage /></AuthRoute>} />

      {/* Organization landing pages — /<slug>, resolved from the orgs table at
          request time (see pages/OrgLanding.tsx). Declared last: every static
          route above still wins, since React Router ranks static segments over
          dynamic ones. A slug with no matching org redirects to "/", so this
          behaves like the catch-all below for anything that isn't an org.

          The DEFAULT org (BusinessLocating) is not served here — it owns "/".
          The page renders its own nav/footer, because the nav carries the
          organization's logo and that isn't known until the org is fetched. */}
      <Route path="/:orgSlug" element={<OrgLandingPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID || "";

  return (
    <GoogleOAuthProvider clientId={googleClientId}>
      <BrowserRouter>
        <PostHogPageTracker />
        <AuthProvider>
          {/* Guest sessions are independent of AuthProvider — a share recipient
              has no account. Nested so guest pages can read their session
              without members and guests clobbering each other's tokens. */}
          <GuestProvider>
            <ToastProvider>
              <NotificationsProvider>
              {/* Above BOTH AppRoutes and ChatbotGate: the drawer is rendered by
                  ChatbotGate, but the "Ask Niva" button that opens it lives in
                  the top bar (Navigation), inside the routes. */}
              <NivaProvider>
              <div className="app-shell min-h-screen bg-background flex flex-col overflow-x-clip">
                <AppRoutes />
                {/* Slide-in alerts for every signed-in role. The provider owns
                    the poll; this only renders what it queues. */}
                <NotificationPopups />
                <BuyerPreferencesGate />
                <AnnouncementGate />
                <NotificationDigestGate />
                <SessionGuard />
                {/* Mounted globally so it's available app-wide. ChatbotGate owns
                    the per-session mount lifecycle (see its docstring); the
                    switch (GET /chatbot/config) is checked inside ChatbotBubble. */}
                <ChatbotGate />
              </div>
              </NivaProvider>
              </NotificationsProvider>
            </ToastProvider>
          </GuestProvider>
        </AuthProvider>
      </BrowserRouter>
    </GoogleOAuthProvider>
  );
}
